<template>
    <DataTable :value="customFields" showGridlines lazy paginator :rows="rows" :totalRecords="totalRecords"
        :first="first" :loading="loading" :filters="filters" filterDisplay="menu" scrollable
        scrollHeight="calc(100vh - 16rem)" :sortField="sortField" :sortOrder="sortOrder" @page="$emit('page', $event)"
        @filter="$emit('filter', $event)" @sort="$emit('sort', $event)">
        <template #loading></template>
        <template #empty>
            <div class="text-center py-12 text-gray-500">
                <i class="fa-regular fa-list text-3xl mb-3"></i>
                <p class="font-semibold">{{ t('customFields.noCustomFields') }}</p>
            </div>
        </template>

        <template #header>
            <div class="flex justify-between items-center">
                <Button icon="fa-solid fa-plus" :label="t('customFields.new')" @click="$emit('create')" />
                <Button icon="fa-solid fa-filter-circle-xmark" :label="t('clearFilters')"
                    @click="$emit('clearFilters')" />
            </div>
        </template>

        <!-- TITLE -->
        <Column field="title" :header="t('customFields.titleField')" sortable filter
            :filterMatchModeOptions="textMatchModeOptions">
            <template #body="{ data }">
                <Skeleton v-if="loading" width="100px" height="1rem" />
                <span v-else>{{ data.title }}</span>
            </template>
            <template #filter="{ filterModel }">
                <InputText v-model="filterModel.value" type="text" :placeholder="t('customFields.searchByTitle')" />
            </template>
        </Column>

        <!-- DESCRIPTION -->
        <Column field="description" :header="t('customFields.description')" sortable>
            <template #body="{ data }">
                <Skeleton v-if="loading" width="150px" height="1rem" />
                <span v-else>{{ data.description || '-' }}</span>
            </template>
        </Column>

        <!-- STATUS -->
        <Column field="enabled" :header="t('status.title')" sortable filter
            :filterMatchModeOptions="selectMatchModeOptions">
            <template #body="{ data }">
                <Skeleton v-if="loading" width="100px" height="1rem" />
                <Tag v-else :value="data.enabled ? t('common.enabled') : t('common.disabled')"
                    :severity="data.enabled ? 'success' : 'danger'" />
            </template>
            <template #filter="{ filterModel }">
                <Select v-model="filterModel.value" :options="statusOptions" optionLabel="label" optionValue="value" />
            </template>
        </Column>

        <!-- ACTIONS -->
        <Column headerStyle="width:4rem" :header="t('actions')">
            <template #body="{ data }">
                <Skeleton v-if="loading" width="100px" height="1rem" />
                <div v-else class="flex justify-center">
                    <i class="fa-solid fa-edit fa-lg text-primary cursor-pointer" @click="$emit('edit', data)"></i>
                    <i class="fa-regular fa-trash-can fa-lg text-red-500 cursor-pointer ml-4"
                        @click="$emit('delete', data)"></i>
                </div>
            </template>
        </Column>
    </DataTable>
</template>

<script setup lang="ts">
import { useFilterMatchModeOptions } from '@/composables/useFilterMatchModeOptions';
import type { CustomField } from '@/lib/types/customField';
import type { DataTableFilterEvent, DataTableSortEvent } from 'primevue/datatable';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface Props {
    customFields: CustomField[];
    loading: boolean;
    filters: any;
    sortField?: string;
    sortOrder: number;
    first: number;
    rows: number;
    totalRecords: number;
}

defineProps<Props>();

defineEmits<{
    page: [event: any];
    filter: [event: DataTableFilterEvent];
    sort: [event: DataTableSortEvent];
    clearFilters: [];
    create: [];
    edit: [customField: CustomField];
    delete: [customField: CustomField];
}>();

const {
    textMatchModeOptions,
    selectMatchModeOptions
} = useFilterMatchModeOptions();

const statusOptions = computed(() => [
    { label: t('common.enabled'), value: true },
    { label: t('common.disabled'), value: false }
]);
</script>
